import {
  formatBrandingBusinessAddressEntry,
  formatBrandingLegalAddress,
  formatBrandingPostalAddress,
  findBrandingBusinessAddressBySlug,
  findBrandingEmailBySlug,
  findBrandingTelephoneBySlug,
  LAYOUT_COMPONENT_TYPE,
  listBrandingEmailEntries,
  listBrandingTelephoneEntries,
  listSluggedBrandingBusinessAddresses,
  listSluggedBrandingEmails,
  listSluggedBrandingTelephones,
  resolveBrandingLegalContactEmail,
  resolveBrandingLegalTelephone,
  type LayoutBlock,
  type LocalBusinessBranding,
} from './studio';
import {
  isLegalProseTokenKey,
  resolveBindingForRole,
  type FieldBindingRef,
} from './fieldBindings';
import { LEGAL_BODY_HTML_FIELD_KEY } from './legalBlockFields';
import { expandLegalBodyHtmlTokens } from './legalBodyHtml';
import { projectSocialLinksOntoContent } from './socialLinks';
import { formatOpeningHoursSchedule } from './openingHoursFormat';

type BlockContent = Record<string, Record<string, string>>;

export type PersonaBindingContext = {
  persona: LocalBusinessBranding;
  businessName: string;
  locales: string[];
};

/** Sentinel written when a bound PDL field has no persona value (legal DL rows). */
export const PDL_MISSING_VALUE = '—' as const;

/** True for empty / whitespace / the legal missing-value sentinel. */
export function isPdlMissingValue(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  return !trimmed || trimmed === PDL_MISSING_VALUE;
}

function formatTelephoneDisplay(telephone: string): string {
  if (telephone.startsWith('+49') && telephone.length >= 6) {
    const rest = telephone.slice(3);
    return `+49 ${rest.replace(/(\d{2,4})(?=\d)/g, '$1 ').trim()}`;
  }
  return telephone;
}

function formatTelephoneEntryDisplay(
  entry: { telephone: string; description?: string } | null,
): string {
  if (!entry) return '';
  const phone = formatTelephoneDisplay(entry.telephone);
  return entry.description ? `${phone} (${entry.description})` : phone;
}

function formatTelephonesDisplay(persona: LocalBusinessBranding): string {
  return listBrandingTelephoneEntries(persona)
    .map((entry) => formatTelephoneEntryDisplay(entry))
    .join(' · ');
}

function formatEmailEntryDisplay(
  entry: { email: string; description?: string } | null,
): string {
  if (!entry) return '';
  return entry.description ? `${entry.email} (${entry.description})` : entry.email;
}

function formatEmailsDisplay(persona: LocalBusinessBranding): string {
  return listBrandingEmailEntries(persona)
    .map((entry) => formatEmailEntryDisplay(entry))
    .join(' · ');
}

function summarizeOpeningHoursForLocale(
  persona: LocalBusinessBranding,
  locale: string,
  weekday: 'short' | 'long' = 'short',
): string {
  const formatted = formatOpeningHoursSchedule(persona.openingHours, locale, { weekday });
  return formatted || PDL_MISSING_VALUE;
}

function pdlLocalizedRecord(
  locales: string[],
  build: (locale: string) => string,
): Record<string, string> {
  const record: Record<string, string> = {};
  for (const locale of locales) {
    record[locale] = build(locale);
  }
  return record;
}

type HydrateCtx = PersonaBindingContext & {
  fieldBindings: LayoutBlock['fieldBindings'];
};

function parseLegacyIndex(ref: string, prefix: string): number | null {
  if (!ref.startsWith(prefix)) return null;
  const suffix = ref.slice(prefix.length);
  if (!/^[1-5]$/.test(suffix)) return null;
  return Number(suffix);
}

function resolveBindingValue(
  ref: FieldBindingRef,
  ctx: PersonaBindingContext,
  locale: string,
  options?: { openingHoursWeekday?: 'short' | 'long' },
): string {
  const { persona, businessName } = ctx;

  const telephoneSlug = /^pdl\.telephone-(.+)$/.exec(ref);
  if (telephoneSlug) {
    return (
      formatTelephoneEntryDisplay(findBrandingTelephoneBySlug(persona, telephoneSlug[1]!)) || PDL_MISSING_VALUE
    );
  }
  const legacyPhone = parseLegacyIndex(ref, 'pdl.telephone');
  if (legacyPhone != null) {
    const entry = listBrandingTelephoneEntries(persona)[legacyPhone - 1] ?? null;
    return formatTelephoneEntryDisplay(entry) || PDL_MISSING_VALUE;
  }

  const emailSlug = /^pdl\.contactEmail-(.+)$/.exec(ref);
  if (emailSlug) {
    return formatEmailEntryDisplay(findBrandingEmailBySlug(persona, emailSlug[1]!)) || PDL_MISSING_VALUE;
  }
  const legacyEmail = parseLegacyIndex(ref, 'pdl.contactEmail');
  if (legacyEmail != null) {
    const entry = listBrandingEmailEntries(persona)[legacyEmail - 1] ?? null;
    return formatEmailEntryDisplay(entry) || PDL_MISSING_VALUE;
  }

  const storeSlug = /^pdl\.storeAddress-(.+)$/.exec(ref);
  if (storeSlug) {
    const entry = findBrandingBusinessAddressBySlug(persona, storeSlug[1]!);
    return entry ? formatBrandingBusinessAddressEntry(entry) : PDL_MISSING_VALUE;
  }
  const legacyBiz = parseLegacyIndex(ref, 'pdl.businessAddress');
  if (legacyBiz != null) {
    const entry = listSluggedBrandingBusinessAddresses(persona)[legacyBiz - 1];
    return entry ? formatBrandingBusinessAddressEntry(entry) : PDL_MISSING_VALUE;
  }

  switch (ref) {
    case 'config.businessName':
      // Place name from Google & AI (Persona `legalName`); config is fallback only.
      return persona.legalName?.trim() || businessName.trim() || PDL_MISSING_VALUE;
    case 'pdl.legalName':
      return persona.legalName?.trim() || PDL_MISSING_VALUE;
    case 'pdl.representative':
      return persona.representative?.trim() || PDL_MISSING_VALUE;
    case 'pdl.address': {
      const entry = findBrandingBusinessAddressBySlug(persona, 'primary');
      return entry ? formatBrandingBusinessAddressEntry(entry) : PDL_MISSING_VALUE;
    }
    case 'pdl.legalAddress':
      return formatBrandingLegalAddress(persona) || PDL_MISSING_VALUE;
    case 'pdl.legalTelephone': {
      const phone = resolveBrandingLegalTelephone(persona);
      return phone ? formatTelephoneDisplay(phone) : PDL_MISSING_VALUE;
    }
    case 'pdl.legalContactEmail':
      return resolveBrandingLegalContactEmail(persona) || PDL_MISSING_VALUE;
    case 'pdl.telephone':
      return formatTelephonesDisplay(persona) || PDL_MISSING_VALUE;
    case 'pdl.contactEmail':
      return formatEmailsDisplay(persona) || PDL_MISSING_VALUE;
    case 'pdl.registerCourt':
      return persona.registerCourt?.trim() || PDL_MISSING_VALUE;
    case 'pdl.registerNumber':
      return persona.registerNumber?.trim() || PDL_MISSING_VALUE;
    case 'pdl.vatId':
      return persona.vatId?.trim() || PDL_MISSING_VALUE;
    case 'pdl.openingHours':
      return summarizeOpeningHoursForLocale(
        persona,
        locale,
        options?.openingHoursWeekday ?? 'short',
      );
    default:
      return PDL_MISSING_VALUE;
  }
}

function bindRoleToContent(
  content: BlockContent,
  role: string,
  ctx: HydrateCtx,
  overrideRef?: FieldBindingRef | null,
  options?: { emptyAsMissing?: boolean; openingHoursWeekday?: 'short' | 'long' },
): void {
  const ref =
    overrideRef !== undefined ? overrideRef : resolveBindingForRole(ctx.fieldBindings, role);
  if (!ref) {
    return;
  }
  const emptyAsMissing = options?.emptyAsMissing !== false;
  try {
    // Replace (don't merge) so stale @tokens / prior locale drafts can't win on canvas.
    content[role] = pdlLocalizedRecord(ctx.locales, (locale) => {
      const value = resolveBindingValue(ref, ctx, locale, {
        openingHoursWeekday: options?.openingHoursWeekday,
      });
      if (!emptyAsMissing && value === PDL_MISSING_VALUE) {
        return '';
      }
      return value;
    });
  } catch {
    // One bad PDL field must not abort hydrate for sibling roles (e.g. legalName).
  }
}

/** Build token table for prose expansion (`@legalName`, `@telephone-support`, …). */
export function buildLegalProseTokenValues(
  persona: LocalBusinessBranding,
  businessName: string,
  locale: string,
): Record<string, string> {
  const primaryStore = findBrandingBusinessAddressBySlug(persona, 'primary');
  const tokens: Record<string, string> = {
    legalName: persona.legalName?.trim() || PDL_MISSING_VALUE,
    businessName: persona.legalName?.trim() || businessName.trim() || PDL_MISSING_VALUE,
    representative: persona.representative?.trim() || PDL_MISSING_VALUE,
    address: primaryStore ? formatBrandingPostalAddress(primaryStore.address) : PDL_MISSING_VALUE,
    legalAddress: formatBrandingLegalAddress(persona) || PDL_MISSING_VALUE,
    legalTelephone: (() => {
      const phone = resolveBrandingLegalTelephone(persona);
      return phone ? formatTelephoneDisplay(phone) : PDL_MISSING_VALUE;
    })(),
    legalContactEmail: resolveBrandingLegalContactEmail(persona) || PDL_MISSING_VALUE,
    contactPhone: formatTelephonesDisplay(persona) || PDL_MISSING_VALUE,
    contactEmail: formatEmailsDisplay(persona) || PDL_MISSING_VALUE,
    registerCourt: persona.registerCourt?.trim() || PDL_MISSING_VALUE,
    registerNumber: persona.registerNumber?.trim() || PDL_MISSING_VALUE,
    vatId: persona.vatId?.trim() || PDL_MISSING_VALUE,
    hours: summarizeOpeningHoursForLocale(persona, locale),
  };

  for (const entry of listSluggedBrandingTelephones(persona)) {
    tokens[`telephone-${entry.slug}`] = formatTelephoneEntryDisplay(entry) || PDL_MISSING_VALUE;
  }
  for (const entry of listSluggedBrandingEmails(persona)) {
    tokens[`contactEmail-${entry.slug}`] = formatEmailEntryDisplay(entry) || PDL_MISSING_VALUE;
  }
  for (const entry of listSluggedBrandingBusinessAddresses(persona)) {
    tokens[`storeAddress-${entry.slug}`] = formatBrandingBusinessAddressEntry(entry) || PDL_MISSING_VALUE;
  }

  return tokens;
}

/** Replace `@token` sugar in CDL legal prose. Unknown tokens stay literal. */
export function expandLegalProseTokens(
  text: string,
  tokens: Record<string, string>,
): string {
  return text.replace(/@([a-zA-Z][a-zA-Z0-9-]*)/g, (match, key: string) => {
    if (!isLegalProseTokenKey(key)) {
      return match;
    }
    const value = tokens[key];
    return value && value !== PDL_MISSING_VALUE ? value : PDL_MISSING_VALUE;
  });
}

function expandProseFields(
  content: BlockContent,
  keys: string[],
  ctx: HydrateCtx,
): void {
  for (const key of keys) {
    const localized = content[key];
    if (!localized) {
      continue;
    }
    const next: Record<string, string> = { ...localized };
    for (const locale of Object.keys(localized)) {
      const raw = localized[locale];
      if (!raw || !raw.includes('@')) {
        continue;
      }
      const tokens = buildLegalProseTokenValues(ctx.persona, ctx.businessName, locale);
      next[locale] =
        key === LEGAL_BODY_HTML_FIELD_KEY
          ? expandLegalBodyHtmlTokens(raw, tokens, PDL_MISSING_VALUE)
          : expandLegalProseTokens(raw, tokens);
    }
    content[key] = next;
  }
}

function projectSocialLinksIfBound(content: BlockContent, ctx: HydrateCtx): void {
  if (!resolveBindingForRole(ctx.fieldBindings, 'socialLinks')) {
    return;
  }
  projectSocialLinksOntoContent(content, ctx.persona.socialLinks, ctx.locales);
}

function hydrateFooterPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  // Optional contact lines: omit missing values (do not paint legal-style "—").
  const optional = { emptyAsMissing: false } as const;
  /** Colophon is centered with room for full weekday names (Montag – Freitag). */
  const hoursWeekday =
    block.registryBlockKey === 'Footer_Colophon' ? ('long' as const) : ('short' as const);
  bindRoleToContent(content, 'phone', ctx, undefined, optional);
  bindRoleToContent(content, 'address', ctx, undefined, optional);
  bindRoleToContent(content, 'hours', ctx, undefined, {
    ...optional,
    openingHoursWeekday: hoursWeekday,
  });
  bindRoleToContent(content, 'email', ctx, undefined, optional);
  projectSocialLinksIfBound(content, ctx);
  return { ...block, content };
}

/** Navigation drawer social icons — same Local Presence URLs as footer. */
function hydrateNavigationPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  projectSocialLinksIfBound(content, ctx);
  return { ...block, content };
}

/** Floating contact bar — phone + socials from Local Presence (bindable). */
function hydrateContactChannelsPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  const optional = { emptyAsMissing: false } as const;
  bindRoleToContent(content, 'phone', ctx, undefined, optional);
  projectSocialLinksIfBound(content, ctx);
  return { ...block, content };
}

/** Contact page — address / phone / email / hours from Local Presence (bindable). */
function hydrateContactBlockPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  const optional = { emptyAsMissing: false } as const;
  bindRoleToContent(content, 'phone', ctx, undefined, optional);
  bindRoleToContent(content, 'address', ctx, undefined, optional);
  bindRoleToContent(content, 'hours', ctx, undefined, optional);
  bindRoleToContent(content, 'email', ctx, undefined, optional);
  return { ...block, content };
}

/** Utility Bar — phone / email / address / social from Local Presence. */
function hydrateUtilityBarPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  const optional = { emptyAsMissing: false } as const;
  bindRoleToContent(content, 'phone', ctx, undefined, optional);
  bindRoleToContent(content, 'email', ctx, undefined, optional);
  bindRoleToContent(content, 'address', ctx, undefined, optional);
  projectSocialLinksIfBound(content, ctx);
  return { ...block, content };
}

function hydrateLegalImprintPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  for (const role of [
    'legalName',
    'representative',
    'contactPhone',
    'contactEmail',
    'registerCourt',
    'registerNumber',
    'vatId',
  ] as const) {
    bindRoleToContent(content, role, ctx);
  }
  const addressRef =
    ctx.fieldBindings == null
      ? ('pdl.legalAddress' as const)
      : resolveBindingForRole(ctx.fieldBindings, 'address');
  bindRoleToContent(content, 'address', ctx, addressRef);
  try {
    expandProseFields(
      content,
      ['widerrufText', 'productQualityText', LEGAL_BODY_HTML_FIELD_KEY],
      ctx,
    );
  } catch {
    // Prose expansion is best-effort — bound PDL rows above must still land.
  }
  return { ...block, content };
}

function hydrateLegalPrivacyPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const content: BlockContent = { ...block.content };
  bindRoleToContent(content, 'controllerName', ctx);
  bindRoleToContent(content, 'controllerAddress', ctx);
  bindRoleToContent(content, 'contactEmail', ctx);
  expandProseFields(
    content,
    ['purposes', 'legalBasis', 'retention', 'rights', LEGAL_BODY_HTML_FIELD_KEY],
    ctx,
  );
  return { ...block, content };
}

function hydrateBlockPersona(block: LayoutBlock, ctx: HydrateCtx): LayoutBlock {
  const nextCtx: HydrateCtx = { ...ctx, fieldBindings: block.fieldBindings };
  switch (block.componentType) {
    case LAYOUT_COMPONENT_TYPE.footerStandard:
      return hydrateFooterPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.navigationHeader:
      return hydrateNavigationPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.contactChannelsFloat:
      return hydrateContactChannelsPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.contactBlock:
      return hydrateContactBlockPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.utilityBar:
      return hydrateUtilityBarPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.legalImprintBlock:
      return hydrateLegalImprintPersona(block, nextCtx);
    case LAYOUT_COMPONENT_TYPE.legalPrivacyBlock:
      return hydrateLegalPrivacyPersona(block, nextCtx);
    default:
      return block;
  }
}

function hydrateBlocks(blocks: LayoutBlock[], ctx: HydrateCtx): LayoutBlock[] {
  return blocks.map((block) => {
    try {
      return hydrateBlockPersona(block, ctx);
    } catch {
      // Keep the draft block rather than failing the whole BLOCKS_SYNC payload.
      return block;
    }
  });
}

/** Preview / draft hydrate — merges bound PDL values without mutating stored drafts. */
export function hydrateLayoutBlocksWithPersona(
  blocks: LayoutBlock[],
  persona: LocalBusinessBranding | null | undefined,
  options: { businessName?: string; locales: string[] },
): LayoutBlock[] {
  if (!persona || blocks.length === 0) {
    return blocks;
  }
  const ctx: HydrateCtx = {
    persona,
    businessName: options.businessName ?? '',
    locales: options.locales,
    fieldBindings: undefined,
  };
  return hydrateBlocks(blocks, ctx);
}
